import {
  Input,
  Button,
  NextReviewInput,
  Card,
  PageHeader,
  InlineEdit,
  Heading,
  Select,
  NEXT_REVIEW_DATE_EMPTY_VALUE,
  NextReviewValue,
  DatePicker,
  Progress,
} from '@lmc/ui-kit'
import TextArea from '@lmc/ui-kit/src/dataEntry/Input/components/TextArea/TextArea'
import { Divider, Form, FormInstance } from 'antd'
import { NAMESPACE } from 'locale'
import { goalsConstants } from 'locale/goals'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import dayjs from 'dayjs'
import ActionsInput from '../ActionPlanInput/ActionPlanInput'
import { GoalResponse, GoalStatus, LogCategoryItem } from '@lmc/ts-common'
import { ActionPlanItemValue } from '../ActionPlanInput/ActionPlanItem'
import { FC, useEffect, useState } from 'react'
import FormItem from '../FormItem/FormItem'
import { Link, useNavigate } from 'react-router-dom'
import { GOALS } from 'components/routes/residents/goals'
import { getRouteWithParams } from 'components/routes/utils'
import GoalsSignature from '../GoalsSignature/GoalsSignature'
import CarePlanInput from '../SupportPlanInput/CarePlanInput'
import DocumentsInput from '../DocumentsInput/DocumentsInput'
import useCarers from 'hooks/useFindItem/useCarers'
import { useSelector } from 'react-redux'
import { showToast } from 'common/utils'
import { CommonErrors } from '@lmc/ts-common'
import {
  CarePlansInputProps,
  getToastError,
  hasActionPlanTitleError,
  transformGoalCarePlanIntoPlan,
} from '../../utils'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import { Text } from '@lmc/ui-kit/src/general'
import { Icon } from 'components/generic'
import AutomaticLogLinking from '../AutomaticLogLinking/AutomaticLogLinking'
import GoalCompletedModal from 'screens/Reports/Goals/GoalsDashboard/modals/GoalCompletedModal'

const DESCRIPTION_TEXTAREA_ROWS = 4
const NOTES_TEXTAREA_ROWS = 3

const GoalForm: FC<GoalFormProps> = ({
  onFinish,
  residentID,
  defaultValue = undefined,
  isSubmitting = false,
}) => {
  const { t } = useTranslation(NAMESPACE.GOALS)
  const preferredCarePlansNominative: 'Care Plans' | 'Support Plans' =
    useSelector((state: any) => state.global.home.preferredCarePlansNominative)

  const [form] = Form.useForm<FormResult>()

  const goalID = defaultValue?.id

  const navigate = useNavigate()

  const initialProgress = Number(defaultValue?.progress || '0')
  const [addedProgress, setAddedProgress] = useState<number | string>('')
  const [currentProgress, setCurrentProgress] =
    useState<number>(initialProgress)

  const {
    'log-category-items': categoryItems,
  }: {
    'log-category-items': LogCategoryItem[] | undefined
  } = useSelector(
    (state: {
      data: {
        'log-category-items': LogCategoryItem[]
      }
    }) => state.data,
  )
  const initialCategoryLogs = constructCategories(
    defaultValue?.logCategoryItems || [],
    categoryItems,
  )
  const [logsLinked, setLogsLinked] = useState(initialCategoryLogs)

  const finishHandler = async (values: FormResult) => {
    const progressBeforeReview = defaultValue?.progress || 0
    if (
      currentProgress === 100 &&
      progressBeforeReview < 100 &&
      !showCompletedModal
    ) {
      setShowCompletedModal(true)
      return
    }

    setIsModified(false)

    let nextReviewValue: string | null = null
    if (values.nextReviewField !== NEXT_REVIEW_DATE_EMPTY_VALUE) {
      nextReviewValue = values.nextReviewField.format('YYYY-MM-DD')
    }
    const formattedLogsLinked = [...new Set(Object.values(logsLinked).flat())]

    const newValues: FormResultValues = {
      ...values,
      resident: residentID,
      actions: values.actionPlan,
      startDate: values.startDay.format('YYYY-MM-DD'),
      endDate: values.endDay.format('YYYY-MM-DD'),
      nextReviewDate: nextReviewValue,
      // @ts-ignore
      carePlans: values?.supportPlans?.map((carePlan) => carePlan.id) || [],
      documents: values.documents,
      progress: currentProgress,
      logCategoryItems: formattedLogsLinked,
    }

    try {
      const response = await onFinish(newValues)

      if (response?.result) {
        showToast({ title: t(goalsConstants.SAVED) })
        const newGoalID = response.result.id

        navigate(
          getRouteWithParams(GOALS.VIEW, { id: residentID, goalID: newGoalID }),
        )
      }
    } catch (err) {
      const toastError = getToastError(err as CommonErrors.Dto, t)

      showToast({ type: 'error', title: toastError })
    }
  }

  const isEdit = Boolean(defaultValue)

  const getCancelButtonLink = () => {
    if (isEdit) {
      return getRouteWithParams(GOALS.VIEW, {
        goalID,
        id: residentID,
      })
    }

    return getRouteWithParams(GOALS.INDEX, {
      id: residentID,
    })
  }

  const { all: carers } = useCarers({})

  const [isModified, setIsModified] = useState(false)
  function handleValuesChange() {
    if (!isModified && form.isFieldsTouched()) {
      setIsModified(true)
    }
  }

  const statusValue = form.getFieldValue('status')
  const [changedStatus, setChangedStatus] = useState<string | null>(null)
  const [isAutoUpdate, setIsAutoUpdate] = useState(false)

  useEffect(() => {
    if (isAutoUpdate) {
      setIsAutoUpdate(false)
    } else {
      setChangedStatus(null)
    }
  }, [statusValue])

  const handleProgress = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    const newNumberValue = Number(newValue)
    if (
      newValue === '' ||
      (/^\d+$/.test(newValue) && newNumberValue >= 0 && newNumberValue <= 100)
    ) {
      const totalProgress = initialProgress + newNumberValue
      const currentProgress = totalProgress > 100 ? 100 : totalProgress
      setCurrentProgress(currentProgress)
      const addedProgress = newNumberValue === 0 ? '' : newNumberValue
      setAddedProgress(addedProgress)

      if (
        Number(addedProgress) === 0 &&
        defaultValue?.status === GoalStatus.notStarted
      ) {
        form.setFieldValue('status', GoalStatus.notStarted)
        setChangedStatus(null)
        setIsAutoUpdate(true)
      }

      if (Number(addedProgress) > 0) {
        form.setFieldValue('status', GoalStatus.inProgress)
        if (defaultValue?.status !== GoalStatus.inProgress) {
          setChangedStatus(GoalStatus.inProgress)
        } else {
          setChangedStatus(null)
        }
        setIsAutoUpdate(true)
      }

      if (Number(currentProgress) === 100) {
        form.setFieldValue('status', GoalStatus.done)
        if (defaultValue?.status !== GoalStatus.done) {
          setChangedStatus(GoalStatus.done)
        } else {
          setChangedStatus(null)
        }
        setIsAutoUpdate(true)
      }
    }
  }

  const statusOptions = Object.keys(GoalStatus).map((status) => ({
    label: t(goalsConstants[status as GoalStatus]),
    value: status,
  }))

  const milestonesActions = Object.keys(GoalStatus).filter(
    (key) => GoalStatus[key as keyof typeof GoalStatus] !== GoalStatus.archive,
  )

  const [showCompletedModal, setShowCompletedModal] = useState(false)

  return (
    <Form<FormResult>
      layout="vertical"
      onFinish={finishHandler}
      name="goal-form"
      form={form}
      initialValues={defaultValue}
      onValuesChange={() => handleValuesChange()}
      scrollToFirstError
    >
      <NavigationModal canNavigate={!isModified} />
      {isEdit ? (
        <PageHeader
          title={
            <Form.Item
              name="title"
              rules={[
                {
                  required: true,
                  message: t(goalsConstants.REQUIRED_MESSAGE),
                },
              ]}
            >
              <InlineEdit
                textComponent={(props) => <Heading level={1} {...props} />}
                defaultEditState={false}
                displayActions={false}
              />
            </Form.Item>
          }
          sticky
        />
      ) : (
        <StyledCard extraMargin="32px 0">
          <FormItem
            name="title"
            rules={[
              {
                required: true,
                message: t(goalsConstants.REQUIRED_MESSAGE),
              },
            ]}
          >
            <Input
              placeholder={t(goalsConstants.ADD_A_GOAL_TITLE)}
              data-cy="input-goal-title"
            />
          </FormItem>
        </StyledCard>
      )}
      <Wrapper>
        <StyledCard>
          <FormItem
            name="description"
            label={t(goalsConstants.DESCRIPTION)}
            rules={[
              { required: true, message: t(goalsConstants.REQUIRED_MESSAGE) },
            ]}
            required
            description={t(goalsConstants.DESCRIPTION_FIELD_DESCRIPTION)}
            titleLevel={3}
            descriptionLevel={1}
          >
            <StyledTextArea
              placeholder={t(goalsConstants.START_TYPING)}
              rows={DESCRIPTION_TEXTAREA_ROWS}
            />
          </FormItem>
        </StyledCard>
        <StyledCard>
          <FormItem
            name="successMeasurement"
            rules={[
              { required: true, message: t(goalsConstants.REQUIRED_MESSAGE) },
            ]}
            required
            label={t(goalsConstants.MEASURE_OF_SUCCESS)}
            description={t(goalsConstants.MEASURE_OF_SUCCESS_DESCRIPTION)}
            titleLevel={3}
            descriptionLevel={1}
          >
            <StyledTextArea placeholder={t(goalsConstants.START_TYPING)} />
          </FormItem>
        </StyledCard>
        <StyledCard>
          <Heading level={3}>{t(goalsConstants.GOAL_MANAGEMENT)}</Heading>
          <StyledDivider />
          <InputWrapper>
            <TextMedium level={1}>{`${t(
              goalsConstants.SET_PRORITY,
            )}:`}</TextMedium>
            <ImportanceInput>
              <ImportanceFormItem
                name="importance"
                initialValue={defaultValue?.importance || IMPORTANCE_LEVELS[0]}
              >
                <StyledSelect
                  options={IMPORTANCE_LEVELS.map((level) => ({
                    label: `${t(goalsConstants.LEVEL)} ${level}`,
                    value: level,
                  }))}
                />
              </ImportanceFormItem>
              <TextLight level={1}>
                {t(goalsConstants.IMPORTANCE_DESCRIPTION)}
              </TextLight>
            </ImportanceInput>
          </InputWrapper>

          <StyledCard dark>
            <StyledHeading level={4}>
              {t(goalsConstants.GOAL_DURATION)}
            </StyledHeading>
            <StyledDivider />
            <GoalDuration>
              <InputWrapper>
                <TextMedium>{`${t(
                  goalsConstants.GOAL_START_DATE,
                )}:`}</TextMedium>
                <FormItem
                  name="startDay"
                  rules={[
                    {
                      required: true,
                      message: t(goalsConstants.REQUIRED_MESSAGE),
                    },
                  ]}
                  required
                  initialValue={dayjs(defaultValue?.startDate)}
                >
                  <StyledDatePicker
                    placeholder="Start Date"
                    format="DD MMM YYYY"
                  />
                </FormItem>
              </InputWrapper>
              <InputWrapper>
                <TextMedium>{`${t(goalsConstants.GOAL_END_DATE)}:`}</TextMedium>
                <FormItem
                  name="endDay"
                  rules={[
                    {
                      required: true,
                      message: t(goalsConstants.REQUIRED_MESSAGE),
                    },
                    {
                      validator: (_, value) => {
                        const startDate = form.getFieldValue('startDay')
                        if (!startDate || !value) return Promise.resolve()

                        return dayjs(startDate).isAfter(dayjs(value))
                          ? Promise.reject(
                              new Error('End date must be after start date'),
                            )
                          : Promise.resolve()
                      },
                    },
                  ]}
                  required
                  initialValue={dayjs(defaultValue?.endDate)}
                  style={{ paddingLeft: '5px' }}
                >
                  <StyledDatePicker
                    placeholder="End Date"
                    format="DD MMM YYYY"
                  />
                </FormItem>
              </InputWrapper>
            </GoalDuration>
          </StyledCard>
        </StyledCard>
        <StyledCard>
          <FormItem
            name="actionPlan"
            label={t(goalsConstants.MILESTONES)}
            description={t(goalsConstants.ACTION_PLAN_DESCRIPTION)}
            initialValue={defaultValue?.actions}
            rules={[
              {
                validator: (rule, value) => actionPlanValidator(value, t),
              },
            ]}
            titleLevel={3}
            descriptionLevel={1}
          >
            <ActionsInput
              options={milestonesActions.map((status) => ({
                label: t(goalsConstants[status as GoalStatus]),
                value: status as GoalStatus,
              }))}
              defaultStatusValue={GoalStatus.notStarted}
              variant="text"
              buttonText={t(goalsConstants.ADD_MILESTONE)}
            />
          </FormItem>
        </StyledCard>
        <StyledCard>
          <FormItem<Omit<CarePlansInputProps['value'], 'name'>>
            name="supportPlans"
            label={t(
              goalsConstants[`ASSOCIATED_${preferredCarePlansNominative}`],
            )}
            initialValue={transformGoalCarePlanIntoPlan(
              defaultValue?.carePlans,
              carers,
            )}
          >
            <CarePlanInput disabled={false} />
          </FormItem>
        </StyledCard>
        <StyledCard>
          <FormItem
            name="documents"
            label={t(goalsConstants.ATTACHED_DOCUMENTS)}
          >
            <DocumentsInput />
          </FormItem>
        </StyledCard>
        <AutomaticLogLinking
          logsLinked={logsLinked}
          setLogsLinked={setLogsLinked}
        />
        <StyledCard>
          <HeadingBlock level={3}>
            {t(goalsConstants.GOAL_PROGRESS)}
          </HeadingBlock>
          <TextMedium level={1} noMargin>
            {t(goalsConstants.GOAL_PROGRESS_DESCRIPTION)}
          </TextMedium>
          <StyledDivider />
          <ProgressWrapper>
            <FormItem
              name="progress"
              initialValue={null}
              className="progressFormItem"
            >
              <ProgressInput
                placeholder={t(goalsConstants.PERCENTAGE_PLACEHOLDER)}
                value={addedProgress}
                onChange={handleProgress}
              />
              <Percentage>%</Percentage>
            </FormItem>
            <div>
              <Progress
                percent={currentProgress}
                showInfo={false}
                size={[382, 8]}
              />
              <ProgressBarWrapper>
                <Text weight="medium">
                  {t(goalsConstants.BEFORE_REVIEW, {
                    variable: defaultValue?.progress || 0,
                  })}
                </Text>
                <Text weight="medium">
                  {t(goalsConstants.CURRENT, {
                    variable: currentProgress,
                  })}
                </Text>
              </ProgressBarWrapper>
            </div>
          </ProgressWrapper>
          <StyledCard dark>
            <StyledHeading level={4}>
              {t(goalsConstants.GOAL_STATUS)}
            </StyledHeading>
            {changedStatus && (
              <StatusChangeText>
                {t(goalsConstants.STATUS_CHANGE, {
                  variable: t(goalsConstants[changedStatus as GoalStatus]),
                })}
              </StatusChangeText>
            )}
            <StyledDivider />
            <Form.Item<FormResult>
              name="status"
              initialValue={defaultValue?.status || GoalStatus.notStarted}
            >
              <StyledSelect
                style={{ width: 400, maxWidth: '80vw' }}
                options={statusOptions}
              />
            </Form.Item>
          </StyledCard>
        </StyledCard>
        <Card>
          <FormItem name="notes" label={t(goalsConstants.WRITE_UPDATE)}>
            <NotesTextArea
              placeholder={t(goalsConstants.START_TYPING)}
              rows={NOTES_TEXTAREA_ROWS}
            />
          </FormItem>
          <NextReview>
            <RequiredText weight="medium">
              {t(goalsConstants.NEXT_REVIEW)}
            </RequiredText>
            <Icon fill="black" name="calendar" size={24} />
            <FormItem
              rules={[
                { required: true, message: t(goalsConstants.REQUIRED_MESSAGE) },
                {
                  validator: (rule, value) =>
                    nextReviewValidator(value, form, t),
                },
              ]}
              name="nextReviewField"
              initialValue={NEXT_REVIEW_DATE_EMPTY_VALUE}
            >
              <NextReviewInput
                disabledDate={(date) => date < dayjs().subtract(1, 'day')}
                radioValues={[
                  {
                    value: dayjs().add(1, 'w'),
                    text: t(goalsConstants.ONE_WEEK),
                  },
                  {
                    value: dayjs().add(1, 'M'),
                    text: t(goalsConstants.ONE_MONTH),
                  },
                  {
                    value: dayjs().add(1, 'year'),
                    text: t(goalsConstants.THREE_MONTHS),
                  },
                  {
                    value: NEXT_REVIEW_DATE_EMPTY_VALUE,
                    text: t(goalsConstants.NO_FURTHER_SUPPORT_REQUIRED),
                  },
                ]}
              />
            </FormItem>
          </NextReview>
        </Card>
        <AbsoluteFooter>
          {isEdit ? (
            <GoalsSignature
              buttonChildren={t(goalsConstants.ADD_SIGNATURE)}
              goalTitle={form.getFieldValue('title')}
            />
          ) : (
            <div />
          )}

          <ButtonsWrapper>
            <Link to={getCancelButtonLink()}>
              <Button type="button" data-cy="cancel-goal-btn">
                {t(goalsConstants.CANCEL)}
              </Button>
            </Link>
            <Button
              type="button"
              onClick={form.submit}
              isLoading={isSubmitting}
              variant="primary"
              data-cy="save-goal-btn"
            >
              {t(goalsConstants.SAVE)}
            </Button>
          </ButtonsWrapper>
        </AbsoluteFooter>
      </Wrapper>

      <GoalCompletedModal
        open={showCompletedModal}
        onClose={() => {
          setShowCompletedModal(false)
          finishHandler(form.getFieldsValue())
        }}
      />
    </Form>
  )
}

type GoalFormProps = {
  onFinish: (
    values: FormResultValues,
  ) => Promise<{ result: { id: string } } | undefined>
  residentID: string
  defaultValue?: GoalResponse | undefined
  isSubmitting?: boolean
}

type ImportanceLevels = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10

export type FormResult = {
  actionPlan: ActionPlanItemValue<GoalStatus>[] | undefined
  'date-picker': Date
  description: string
  documents: string[]
  duration: [dayjs.Dayjs, dayjs.Dayjs]
  startDay: dayjs.Dayjs
  endDay: dayjs.Dayjs
  status: GoalStatus
  importance: ImportanceLevels
  nextReviewField: NextReviewValue
  notes?: string
  successMeasurement: string
  supportPlans: CarePlansInputProps['value']
  title: string
}

export interface FormResultValues extends FormResult {
  resident: string
  actions:
    | {
        title: string
        status: GoalStatus
      }[]
    | undefined
  startDate: string
  endDate: string
  nextReviewDate: string | null
  carePlans: string[]
  documents: string[]
  progress: number
  logCategoryItems: string[]
}

const AbsoluteFooter = styled(Card)`
  padding: 20px;
  position: fixed;
  width: calc(100vw - 290px);
  @media (max-width: 768px) {
    width: 100vw;
  }
  right: 0px;
  bottom: 0px;

  border-radius: 0px;
  box-shadow: 5px 0 15px -8px rgba(0, 0, 0, 0.15);

  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-top: 8px;

  // this is for footer
  margin-bottom: 70px;

  h4 {
    color: ${({ theme }) => theme.color.neutral.color3};
    margin-bottom: 16px;
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 8px;

  align-items: center;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
  }
`

const NotesTextArea = styled(TextArea)`
  margin-bottom: 18px;
`

const ImportanceFormItem = styled(FormItem)`
  width: 108px;
`

const StyledTextArea = styled(TextArea)`
  color: ${({ theme }) => theme.color.neutral.color3};
`
const InputWrapper = styled.div`
  display: flex;
  gap: 46px;
  margin-bottom: 16px;
`
const ImportanceInput = styled.div`
  display: flex;
  flex-direction: column;
`

interface TextMediumProps {
  noMargin?: boolean
}

const TextMedium = styled(Text)<TextMediumProps>`
  color: ${({ theme }) => theme.color.neutral.color4};
  margin: ${({ noMargin }) => (noMargin ? 0 : '8px')}
  width: 122px;
`

const StyledHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};

  &::after {
    content: ' *';
    color: #ff0000;
  }
`

const HeadingBlock = styled(Heading)`
  display: block;
  margin-bottom: 4px;
`

const RequiredText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const TextLight = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const StyledSelect = styled(Select)`
  width: 400px !important;
  height: 40px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    max-width: 200px !important;
  }
`

const StyledDatePicker = styled(DatePicker)`
  width: 400px !important;
  height: 40px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    max-width: 200px !important;
  }
`

const StatusChangeText = styled(Text)`
  display: block;
  color: ${({ theme }) => theme.color.status.primary};
`

interface StyledCardProps {
  dark?: boolean
  extraMargin?: string
}

const StyledCard = styled(Card)<StyledCardProps>`
  padding: 24px;
  background: ${({ theme, dark }) =>
    dark ? theme.color.neutral.color10 : theme.color.neutral.color11};
  margin: ${({ extraMargin }) => extraMargin};

  .ant-form-item-control-input {
    min-height: 0 !important;
  }
`

const StyledDivider = styled(Divider)`
  margin: 16px 0;
`

const GoalDuration = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`

const NextReview = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
  }
`
const ProgressInput = styled(Input)`
  width: 168px;
  height: 40px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.color.neutral.color6};
`

const Percentage = styled(Text)`
  display: inline-block;
  box-sizing: border-box;
  height: 40px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.color.neutral.color10};
  border-radius: ${({
    theme: {
      properties: { borderRadius },
    },
  }) => `0 ${borderRadius.small} ${borderRadius.small} 0`};
  border: 1px solid ${({ theme }) => theme.color.neutral.color6};
  border-left: 0px;
`

const ProgressWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;

  .progressFormItem .ant-form-item-control-input-content {
    display: flex !important;
  }

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;

    .ant-progress-outer {
      padding: 20px 0;
      max-width: 350px;
      width: inherit !important;
    }
  }
`

const ProgressBarWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`

const IMPORTANCE_LEVELS: ImportanceLevels[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

export const actionPlanValidator = async (
  value: FormResult['actionPlan'],
  t: (value: string) => string,
) => {
  let allActionPlansHaveATitle = true

  value?.forEach((action) => {
    const hasError = hasActionPlanTitleError(action.title)

    if (hasError) {
      allActionPlansHaveATitle = false
    }
  })

  if (!allActionPlansHaveATitle) {
    return Promise.reject(new Error(t(goalsConstants.ACTION_TITLES_REQUIRED)))
  }

  return Promise.resolve()
}

const nextReviewValidator = async (
  value: string | null,
  form: FormInstance<FormResult>,
  t: (value: string) => string,
) => {
  const today = dayjs().startOf('day')

  if (dayjs(value).isBefore(today)) {
    return Promise.reject(
      new Error(t(goalsConstants.NEXT_REVIEW_VALIDATION_ERROR)),
    )
  }

  return Promise.resolve()
}

type CategoriesResult = {
  [categoryId: string]: string[]
}

const constructCategories = (
  logsLinked: string[],
  categoryItems: LogCategoryItem[],
): CategoriesResult => {
  const result: CategoriesResult = {}

  logsLinked.forEach((itemId) => {
    const categoryItem = categoryItems.find(
      (item) => item.id.toString() === itemId,
    )

    const category = categoryItem?.category?.toString()
    if (category) {
      if (result[category]) {
        result[category].push(itemId)
      } else {
        result[category] = [itemId]
      }
    }
  })

  return result
}

export default GoalForm
